import { useEffect } from 'react';
import './DarkestLariatAnimation.css';

/**
 * PROFESSIONAL DARKEST LARIAT ANIMATION
 * Dark-type physical move - swings both arms in a spinning lariat
 * 
 * Key Features:
 * - Dark-type physical attack (85 base power, 100% accuracy)
 * - Ignores opponent's stat changes
 * - Incineroar signature move with spinning dark energy lariat
 * - Professional gaming quality with spinning contact impact
 */
export default function DarkestLariatAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;

    const elements = [];

    // PHASE 1: Dark Energy Spin-Up (0-400ms)
    playerEl.classList.add('darkest-lariat-user-spin');

    const darkAura = document.createElement('div');
    darkAura.className = 'darkest-lariat-aura';
    darkAura.style.left = `${playerX - 65}px`;
    darkAura.style.top = `${playerY - 65}px`;
    container.appendChild(darkAura);
    elements.push(darkAura);

    // Spinning dark energy ring
    const spinRing = document.createElement('div');
    spinRing.className = 'darkest-lariat-spin-ring';
    spinRing.style.left = `${playerX - 50}px`;
    spinRing.style.top = `${playerY - 50}px`;
    container.appendChild(spinRing);
    elements.push(spinRing);

    // PHASE 2: Spinning Charge (400-750ms)
    const phase2Timer = setTimeout(() => {
      playerEl.classList.add('darkest-lariat-user-dash');
      playerEl.style.setProperty('--dash-x', `${dx * 0.85}px`);
      playerEl.style.setProperty('--dash-y', `${dy * 0.85}px`);

      // Dark energy trail during spin
      for (let i = 0; i < 10; i++) {
        const trailTimer = setTimeout(() => {
          const trail = document.createElement('div');
          trail.className = 'darkest-lariat-trail';
          const progress = i / 10;
          trail.style.left = `${playerX + dx * progress * 0.85 + (Math.random() - 0.5) * 25 - 8}px`;
          trail.style.top = `${playerY + dy * progress * 0.85 + (Math.random() - 0.5) * 25 - 8}px`;
          container.appendChild(trail);
          elements.push(trail);
        }, i * 30);
        elements.push({ timer: trailTimer, type: 'timer' });
      }
    }, 400);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Lariat Impact (750-1250ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('darkest-lariat-opponent-hit');
      playerEl.classList.remove('darkest-lariat-user-dash');

      // Dark impact flash
      const impact = document.createElement('div');
      impact.className = 'darkest-lariat-impact';
      impact.style.left = `${opponentX - 90}px`;
      impact.style.top = `${opponentY - 90}px`;
      container.appendChild(impact);
      elements.push(impact);

      // Dark spinning shockwaves
      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'darkest-lariat-shockwave';
          sw.style.left = `${opponentX - 65}px`;
          sw.style.top = `${opponentY - 65}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 80);
        elements.push({ timer: swTimer, type: 'timer' });
      }

      // Dark sparks
      for (let i = 0; i < 16; i++) {
        const sparkAngle = (i / 16) * 360;
        const sparkDist = 50 + Math.random() * 40;
        const spark = document.createElement('div');
        spark.className = 'darkest-lariat-spark';
        spark.style.left = `${opponentX - 4}px`;
        spark.style.top = `${opponentY - 4}px`;
        spark.style.setProperty('--scatter-x', `${Math.cos(sparkAngle * Math.PI / 180) * sparkDist}px`);
        spark.style.setProperty('--scatter-y', `${Math.sin(sparkAngle * Math.PI / 180) * sparkDist}px`);
        spark.style.animationDelay = `${i * 15}ms`;
        container.appendChild(spark);
        elements.push(spark);
      }
    }, 750);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Settle (1250-1500ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('darkest-lariat-user-spin');
    }, 1250);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1500ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('darkest-lariat-user-spin', 'darkest-lariat-user-dash');
      opponentEl.classList.remove('darkest-lariat-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1500);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('darkest-lariat-user-spin', 'darkest-lariat-user-dash');
      opponentEl.classList.remove('darkest-lariat-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

DarkestLariatAnimation.moveType = 'dark';
